/**
 * Audio File Existence Checker
 * 
 * This utility provides comprehensive checking for audio file existence
 * to ensure we only attempt to play audio when actual files are available.
 */

interface AudioCheckResult {
  exists: boolean;
  isAudio: boolean;
  size?: number;
  contentType?: string;
  error?: string;
}

interface AudioCheckCache {
  [url: string]: {
    result: AudioCheckResult;
    timestamp: number;
  };
}

class AudioFileChecker {
  private cache: AudioCheckCache = {};
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly MIN_AUDIO_SIZE = 1024; // 1KB minimum for valid audio

  /**
   * Check if an audio file exists and is valid
   */
  async checkAudioExists(audioUrl: string): Promise<AudioCheckResult> {
    if (!audioUrl) {
      return {
        exists: false,
        isAudio: false,
        error: 'No audio URL provided'
      };
    }

    // Check cache first
    const cached = this.getCachedResult(audioUrl);
    if (cached) {
      return cached;
    }

    try {
      const result = await this.performAudioCheck(audioUrl);
      
      // Cache the result
      this.cache[audioUrl] = {
        result,
        timestamp: Date.now()
      };

      return result;
    } catch (error) {
      const result: AudioCheckResult = {
        exists: false,
        isAudio: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };

      // Cache negative results too (but for shorter time)
      this.cache[audioUrl] = {
        result,
        timestamp: Date.now()
      };

      return result;
    }
  }

  /**
   * Perform the actual HTTP check for audio file existence
   */
  private async performAudioCheck(audioUrl: string): Promise<AudioCheckResult> {
    const fullUrl = audioUrl.startsWith('/') ? `${window.location.origin}${audioUrl}` : audioUrl;
    
    console.log(`🔍 Checking audio file existence: ${fullUrl}`);

    try {
      const response = await fetch(fullUrl, {
        method: 'HEAD',
        cache: 'no-cache'
      });

      if (!response.ok) {
        return {
          exists: false,
          isAudio: false,
          error: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const contentType = response.headers.get('content-type') || '';
      const contentLength = response.headers.get('content-length');
      const size = contentLength ? parseInt(contentLength, 10) : 0;

      // Check if it's an audio file
      const isAudio = contentType.includes('audio/') || 
                     audioUrl.toLowerCase().endsWith('.mp3') ||
                     audioUrl.toLowerCase().endsWith('.wav') ||
                     audioUrl.toLowerCase().endsWith('.ogg') ||
                     audioUrl.toLowerCase().endsWith('.m4a');

      // Check if file is large enough to be a real audio file
      const isValidSize = size >= this.MIN_AUDIO_SIZE;

      // Special handling for ganji-adabi URLs
      if (audioUrl.includes('ganji-adabi')) {
        return this.checkGanjiAdabiAudio(fullUrl, response);
      }

      const result: AudioCheckResult = {
        exists: true,
        isAudio,
        size,
        contentType
      };

      if (!isAudio) {
        result.error = `Not an audio file (content-type: ${contentType})`;
      } else if (!isValidSize) {
        result.error = `File too small to be valid audio (${size} bytes)`;
        result.exists = false;
      }

      console.log(`✅ Audio check result:`, result);
      return result;

    } catch (error) {
      console.error(`❌ Error checking audio file:`, error);
      throw error;
    }
  }

  /**
   * Special handling for ganji-adabi audio URLs
   */
  private async checkGanjiAdabiAudio(fullUrl: string, response: Response): Promise<AudioCheckResult> {
    const contentType = response.headers.get('content-type') || '';
    
    // If it's a JSON response, it means the server is handling the audio generation
    if (contentType.includes('application/json')) {
      try {
        const data = await response.json();
        if (data.success && data.audioUrl) {
          // Recursively check the actual audio URL
          return await this.checkAudioExists(data.audioUrl);
        } else {
          return {
            exists: false,
            isAudio: false,
            error: data.message || 'Ganji-adabi audio generation failed'
          };
        }
      } catch (error) {
        return {
          exists: false,
          isAudio: false,
          error: 'Failed to parse ganji-adabi response'
        };
      }
    }

    // Direct audio file
    return {
      exists: true,
      isAudio: contentType.includes('audio/'),
      contentType
    };
  }

  /**
   * Get cached result if still valid
   */
  private getCachedResult(audioUrl: string): AudioCheckResult | null {
    const cached = this.cache[audioUrl];
    if (!cached) return null;

    const age = Date.now() - cached.timestamp;
    if (age > this.CACHE_DURATION) {
      delete this.cache[audioUrl];
      return null;
    }

    console.log(`📋 Using cached audio check result for: ${audioUrl}`);
    return cached.result;
  }

  /**
   * Clear the cache
   */
  clearCache(): void {
    this.cache = {};
    console.log('🗑️ Audio check cache cleared');
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; entries: string[] } {
    return {
      size: Object.keys(this.cache).length,
      entries: Object.keys(this.cache)
    };
  }

  /**
   * Check multiple audio URLs in parallel
   */
  async checkMultipleAudioExists(audioUrls: string[]): Promise<Map<string, AudioCheckResult>> {
    const results = new Map<string, AudioCheckResult>();
    
    const promises = audioUrls.map(async (url) => {
      const result = await this.checkAudioExists(url);
      results.set(url, result);
    });

    await Promise.all(promises);
    return results;
  }

  /**
   * Check if audio URL is likely to be a placeholder or invalid
   */
  isLikelyPlaceholder(audioUrl: string): boolean {
    if (!audioUrl) return true;
    
    // Check for common placeholder patterns
    const placeholderPatterns = [
      '/audio/unknown-pathway/unknown-lesson/audio.mp3',
      '/audio/placeholder',
      '/audio/not-found',
      '/audio/missing',
      'placeholder.mp3',
      'not-found.mp3',
      'missing.mp3'
    ];

    return placeholderPatterns.some(pattern => 
      audioUrl.toLowerCase().includes(pattern.toLowerCase())
    );
  }

  /**
   * Get a user-friendly message about audio availability
   */
  getAudioAvailabilityMessage(result: AudioCheckResult, text?: string): string {
    if (result.exists && result.isAudio) {
      return `Audio available for ${text || 'this item'}`;
    }

    if (result.error) {
      return `Audio not available: ${result.error}`;
    }

    return `No audio available for ${text || 'this item'}`;
  }
}

// Create singleton instance
const audioFileChecker = new AudioFileChecker();

// Export functions
export const checkAudioExists = (audioUrl: string): Promise<AudioCheckResult> => {
  return audioFileChecker.checkAudioExists(audioUrl);
};

export const checkMultipleAudioExists = (audioUrls: string[]): Promise<Map<string, AudioCheckResult>> => {
  return audioFileChecker.checkMultipleAudioExists(audioUrls);
};

export const isLikelyPlaceholder = (audioUrl: string): boolean => {
  return audioFileChecker.isLikelyPlaceholder(audioUrl);
};

export const getAudioAvailabilityMessage = (result: AudioCheckResult, text?: string): string => {
  return audioFileChecker.getAudioAvailabilityMessage(result, text);
};

export const clearAudioCheckCache = (): void => {
  audioFileChecker.clearCache();
};

export const getAudioCheckCacheStats = (): { size: number; entries: string[] } => {
  return audioFileChecker.getCacheStats();
};

export type { AudioCheckResult };

export default audioFileChecker;
